import React, {useCallback} from 'react';
import times from 'lodash/times';
import b_ from 'b_';

import {Slider, useSliderState} from 'client/common/components/lego';
import {ToString} from 'client/common/types';

import './age-slider.css';

const ages = [1, 2, 3, 4, 5] as const;

type Age = (typeof ages)[number];
type AgeString = ToString<Age>;
type AgeRange = [number, number];

const MIN: number = Math.min.apply(null, ages);
const MAX: number = Math.max.apply(null, ages);
const MIN_SLIDER_VALUE = MIN - 1;
const MAX_SLIDER_VALUE = MAX + 1;
const STEP = 1;

const b = b_.with('age-slider');

export function convertToAgeArray(val: AgeRange): AgeString[] {
    const [left, right] = val;

    // у слайдера может быть 6 значений, возраст указывается точками/диапазонами которых 5
    const rEdge = Math.min(right, MAX_SLIDER_VALUE) - 1;
    const lEdge = Math.max(left, MIN);

    const range = [lEdge];

    // если выбрали диапазон, добавляем промежуточные значения
    if (lEdge !== rEdge) {
        let curr = lEdge;

        times(rEdge - lEdge - 1, () => {
            curr = curr + 1;
            range.push(curr);
        });

        range.push(rEdge);
    }

    return range.filter(Boolean).map(e => String(e) as AgeString);
}

export function convertToAgeRange(array: string[]): AgeRange {
    return array
        .slice()
        .sort()
        .reduce(
            (acc, curr, currIndex, arr) => {
                const ageNumber = parseInt(curr, 10);

                if (arr.length === 1) {
                    // если одна точка добавляем еще одну, чтобы всегда можно было выбрать диапазон
                    return [ageNumber, ageNumber + 1];
                }

                // слайдеру нужно две точки, передаем только крайние точки
                if (currIndex === 0) {
                    acc.push(ageNumber);
                } else if (currIndex === arr.length - 1) {
                    acc.push(ageNumber + 1);
                }

                return acc;
            },
            [] as unknown as AgeRange
        );
}

export const isValid = (left: number, right: number) => {
    if (left === right) {
        return false;
    }
    if (left < MIN || right < MIN) {
        return false;
    }
    if (left > MAX_SLIDER_VALUE || right > MAX_SLIDER_VALUE) {
        return false;
    }

    return true;
};

type Props = React.ComponentProps<typeof Slider>;

const AGE_LABEL = {
    1: '18',
    2: '24',
    3: '34',
    4: '44',
    5: '54',
    6: '55+'
} as const;

export function AgeSlider({
    age,
    disabled,
    onChange
}: {
    disabled: boolean;
    age: string[];
    onChange: (ageString: AgeString[]) => void;
}) {
    const {value, onInput} = useSliderState({value: convertToAgeRange(age)});

    const handleChange = useCallback(
        (_: any, val: AgeRange) => onChange(convertToAgeArray(val)),
        [onChange]
    );

    const handleRenderTickLabel: Props['renderTickLabel'] = useCallback((prps, LabelTick) => {
        return prps.isFirst ? null : (
            <LabelTick {...prps}>{AGE_LABEL[prps.index as Age] ?? '-'}</LabelTick>
        );
    }, []);

    const handleInput = (e: React.ChangeEvent, range: number[]) => {
        const [left, right] = range;

        if (isValid(left, right)) {
            onInput(e, range);
        } else {
            onInput(e, value);
        }
    };

    return (
        <Slider
            min={MIN_SLIDER_VALUE}
            max={MAX_SLIDER_VALUE}
            filled
            disabled={disabled}
            step={STEP}
            showTicks
            showTickValues
            view='default'
            className={b()}
            renderTickLabel={handleRenderTickLabel}
            value={value}
            onInput={handleInput}
            onChange={handleChange}
        />
    );
}
